import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clapperboard, Mic, Images, FileText, Film, CheckCircle2, Circle, Clock, Play, Users, ChevronRight, Crown, Gift, Download, Loader2, Sparkles, Globe, Lock, MessageSquare, Copy, Check } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { TopBar } from "@/pages/Dashboard";

const TIER_LABEL = { free: "Free plan", story: "Keepsake Story", story_premium: "Story Premium" };

const MODE_LABEL = { living: "Living portrait", in_memory: "In memory" };
const TYPE_LABEL = { solo: "Solo", couple: "Couple", chorus: "Chorus" };
const STATUS_LABEL = { draft: "Draft", collecting: "Collecting", assembling: "Assembling", review: "In review", ready: "Ready" };

function ChapterChip({ ch, onClick }) {
  const done = ch.status === "done";
  const inProgress = ch.status === "in_progress";
  const Icon = done ? CheckCircle2 : inProgress ? Clock : Circle;
  const dot = done ? "hsl(var(--chapter-good))" : inProgress ? "hsl(var(--chapter-warn))" : "hsl(var(--muted-foreground))";
  return (
    <button data-testid="story-chapter-chip" onClick={onClick}
      className="shrink-0 w-[150px] text-left rounded-xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] p-3 hover:shadow-[var(--film-shadow-soft)] transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-[hsl(var(--muted-foreground))]">{ch.code}</span>
        <Icon size={15} style={{ color: dot }} />
      </div>
      <div className="mt-1.5 font-semibold text-sm leading-snug line-clamp-2">{ch.label}</div>
      <div className="mt-1 font-mono text-[11px] text-[hsl(var(--muted-foreground))] tabular-nums">
        {ch.answered_count > 0 ? `${ch.answered_count} answered` : "Not started"}
      </div>
    </button>
  );
}

function StatBlock({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
      <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Icon size={16} /><span className="text-xs uppercase tracking-wide font-semibold">{label}</span></div>
      <div className="mt-2 font-display text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{sub}</div>}
    </div>
  );
}

const fmtT = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

function ReviewPanel({ story, storyId, videoRef, onChange }) {
  const [comments, setComments] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [copied, setCopied] = useState(false);
  const reviewUrl = `${window.location.origin}/sr/${story.slug}`;

  const load = useCallback(async () => {
    try { const r = await api.get(`/stories/${storyId}/comments`); setComments(r.data.comments || []); } catch {}
  }, [storyId]);
  useEffect(() => { load(); }, [load]);

  const toggleReview = async () => {
    await api.patch(`/stories/${storyId}`, { review_open: !story.review_open });
    toast.success(!story.review_open ? "Review link is now open for family" : "Review link closed");
    onChange();
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(reviewUrl); setCopied(true); setTimeout(() => setCopied(false), 1600); toast.success("Review link copied"); }
    catch { toast.error("Could not copy"); }
  };

  const resolve = async (c) => {
    await api.post(`/stories/${storyId}/comments/${c.comment_id}/resolve`);
    toast.success("Marked as resolved");
    load();
  };

  const jump = (t) => { if (videoRef?.current) { videoRef.current.currentTime = t; videoRef.current.play().catch(() => {}); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const open = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);

  return (
    <div className="mt-4 rounded-xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] p-5" data-testid="story-review-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold"><MessageSquare size={17} className="text-[hsl(var(--accent))]" /> Family review notes</div>
        <button onClick={toggleReview} data-testid="review-toggle" className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">
          {story.review_open ? <><Globe size={13} /> Review open</> : <><Lock size={13} /> Review closed</>}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">Share a link so family can watch this draft and leave timestamped notes before the final cut.</p>

      {story.review_open && (
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={reviewUrl} data-testid="review-link-input" className="flex-1 h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--muted))] text-sm text-[hsl(var(--muted-foreground))]" />
          <button onClick={copy} data-testid="review-copy-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-medium hover:bg-[hsl(var(--muted))]">{copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}</button>
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid="review-owner-comments">
        {open.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No open notes yet.</div>}
        {open.map((c) => (
          <div key={c.comment_id} data-testid={`owner-comment-${c.comment_id}`} className="rounded-lg border border-[hsl(var(--border))] p-3 flex items-start gap-3">
            <button onClick={() => jump(c.t_seconds)} className="shrink-0 mt-0.5 font-mono text-xs px-2 py-1 rounded-md bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))] hover:opacity-80">{fmtT(c.t_seconds)}</button>
            <div className="min-w-0 flex-1"><div className="text-sm">{c.text}</div><div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{c.author || "Family"}</div></div>
            <button onClick={() => resolve(c)} data-testid={`owner-resolve-${c.comment_id}`} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"><CheckCircle2 size={13} /> Resolve</button>
          </div>
        ))}
      </div>

      {resolved.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowResolved((v) => !v)} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:underline">{showResolved ? "Hide" : "Show"} {resolved.length} resolved</button>
          {showResolved && (
            <div className="mt-2 space-y-1.5">
              {resolved.map((c) => (
                <div key={c.comment_id} className="rounded-lg border border-[hsl(var(--border))] p-2.5 flex items-center gap-2 opacity-60">
                  <span className="font-mono text-xs">{fmtT(c.t_seconds)}</span><span className="text-sm line-through">{c.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StudioPanel({ story, storyId, onChange }) {
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [busy, setBusy] = useState("");
  const videoRef = useRef(null);
  const tier = story.plan_tier || "free";
  const paid = tier === "story" || tier === "story_premium";

  const loadFilm = useCallback(async () => {
    try { const r = await api.get(`/stories/${storyId}/film`); setFilm(r.data); } catch {}
  }, [storyId]);
  useEffect(() => { loadFilm(); }, [loadFilm]);

  const comp = film?.compilation || {};
  useEffect(() => {
    if (comp.status === "processing") { const t = setInterval(loadFilm, 4000); return () => clearInterval(t); }
  }, [comp.status, loadFilm]);

  const produce = async (teaser) => {
    setBusy(teaser ? "teaser" : "full");
    try { await api.post(`/stories/${storyId}/produce`, { teaser }); toast.success(teaser ? "Creating your teaser…" : "Producing your film…"); loadFilm(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
    finally { setBusy(""); }
  };

  const purchase = async (lookup) => {
    setBusy("buy");
    try {
      const r = await api.post("/payments/checkout", { lookup_key: lookup, story_id: storyId, origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
      else if (r.data.redirect_url) window.location.href = r.data.redirect_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(""); }
  };

  const gift = async () => {
    const email = window.prompt("Gift a Keepsake Story — recipient's email:");
    if (!email) return;
    const name = window.prompt("Their name?") || "";
    const message = window.prompt("A short message (optional):") || "";
    setBusy("gift");
    try {
      const r = await api.post("/payments/checkout", { lookup_key: "story_one_time", origin_url: window.location.origin, gift_recipient_email: email, gift_recipient_name: name, gift_message: message });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(""); }
  };

  const togglePublic = async () => {
    await api.patch(`/stories/${storyId}`, { watch_public: !story.watch_public });
    toast.success(!story.watch_public ? "Film page is now public" : "Film page is now private");
    onChange();
  };

  const ready = comp.status === "ready" && comp.file_id;

  return (
    <section className="mt-8 rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden" data-testid="story-studio">
      <div className="h-2 bg-[hsl(var(--letterbox))]" />
      <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Film size={20} className="text-[hsl(var(--accent))]" /> Story studio</h2>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--secondary))]" data-testid="story-plan-badge">
              {paid && <Crown size={12} className="text-[hsl(var(--accent))]" />} {TIER_LABEL[tier]}
            </span>
          </div>
        </div>

        {/* Purchase CTA for free plan */}
        {!paid && (
          <div className="mt-4 rounded-xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--theme-accent-soft))] p-4" data-testid="story-upgrade-cta">
            <p className="text-sm text-[hsl(var(--theme-accent-ink))]"><strong>Free teaser included.</strong> Unlock the full documentary, Memory Book PDF and forever storage with a one-time purchase.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button data-testid="story-buy-story" onClick={() => purchase("story_one_time")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold disabled:opacity-60">{busy === "buy" ? <Loader2 size={15} className="animate-spin" /> : null} Story — $99</button>
              <button data-testid="story-buy-premium" onClick={() => purchase("story_premium_one_time")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[hsl(var(--accent))] text-[hsl(var(--accent))] text-sm font-semibold">Premium — $249</button>
              <button data-testid="story-gift-button" onClick={gift} disabled={!!busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))]"><Gift size={15} /> Gift it</button>
            </div>
          </div>
        )}

        {/* Producer actions */}
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <button data-testid="studio-script-button" onClick={() => navigate(`/stories/${storyId}/script`)} className="text-left rounded-xl border border-[hsl(var(--border))] p-4 hover:bg-[hsl(var(--muted))] transition-colors">
            <FileText size={18} className="text-[hsl(var(--accent))]" /><div className="mt-2 font-semibold text-sm">Narration script</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{story.script_approved_pct != null ? `${story.script_approved_pct}% approved` : "Draft & approve"}</div>
          </button>
          <button data-testid="studio-teaser-button" onClick={() => produce(true)} disabled={busy === "teaser" || comp.status === "processing"} className="text-left rounded-xl border border-[hsl(var(--border))] p-4 hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-60">
            <Sparkles size={18} className="text-[hsl(var(--accent))]" /><div className="mt-2 font-semibold text-sm">Free 60-sec teaser</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">A taste of the film</div>
          </button>
          <button data-testid="studio-produce-button" onClick={() => produce(false)} disabled={busy === "full" || comp.status === "processing"} className="text-left rounded-xl border-2 border-[hsl(var(--accent))]/40 p-4 hover:bg-[hsl(var(--theme-accent-soft))] transition-colors disabled:opacity-60">
            <Film size={18} className="text-[hsl(var(--accent))]" /><div className="mt-2 font-semibold text-sm">Produce the film{!paid && " 🔒"}</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Full documentary</div>
          </button>
        </div>

        {/* Film status */}
        {comp.status === "processing" && (
          <div className="mt-4 rounded-xl border border-[hsl(var(--border))] p-4" data-testid="studio-film-progress">
            <div className="flex items-center gap-2 text-sm font-semibold"><Loader2 size={15} className="animate-spin text-[hsl(var(--accent))]" /> {comp.stage || "Producing…"}</div>
            <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full bg-[hsl(var(--accent))] transition-[width]" style={{ width: `${Math.max(5, comp.progress || 5)}%` }} /></div>
          </div>
        )}
        {comp.status === "failed" && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="studio-film-error">Something interrupted the render. Please try producing again.</div>}
        {ready && (
          <div className="mt-4 rounded-xl border border-[hsl(var(--film-border))] bg-[hsl(var(--secondary))] p-4" data-testid="studio-film-ready">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 font-semibold text-sm"><CheckCircle2 size={16} className="text-[hsl(var(--chapter-good))]" /> Your film is ready</div>
              <button onClick={togglePublic} className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid="studio-public-toggle">
                {story.watch_public ? <><Globe size={13} /> Public</> : <><Lock size={13} /> Private</>}
              </button>
            </div>
            <video ref={videoRef} src={fileUrl(comp.file_id)} controls className="mt-3 w-full rounded-lg bg-black aspect-video" data-testid="studio-film-video" />
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={fileUrl(comp.file_id)} download className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"><Download size={15} /> Download film</a>
              {story.watch_public && <a href={`/story/${story.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))]"><Play size={15} /> Legacy page</a>}
              {paid && <a href={`${api.defaults.baseURL}/stories/${storyId}/memory-book.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))]"><FileText size={15} /> Memory Book PDF</a>}
            </div>
          </div>
        )}

        {ready && <ReviewPanel story={story} storyId={storyId} videoRef={videoRef} onChange={onChange} />}
      </div>
    </section>
  );
}

export default function StoryDetail() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/stories/${storyId}`);
      setS(r.data);
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Story not found"); navigate("/dashboard"); }
    } finally { setLoading(false); }
  }, [storyId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen"><TopBar /><div className="max-w-6xl mx-auto px-4 sm:px-6 py-12"><div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" /></div></div>;
  if (!s) return null;

  const nextChapter = (s.chapters || []).find((c) => c.status !== "done") || s.chapters?.[0];
  const archive = s.archive_counts || {};
  const archiveTotal = (archive.photo || 0) + (archive.video || 0) + (archive.document || 0) + (archive.voice || 0);

  return (
    <div className="min-h-screen" data-testid="story-detail">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={15} /> Dashboard</button>

        {/* header */}
        <div className="mt-4 rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden">
          <div className="h-2 bg-[hsl(var(--letterbox))]" />
          <div className="p-6 sm:p-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                <Clapperboard size={13} /> {TYPE_LABEL[s.type]} · {MODE_LABEL[s.mode]} · {s.desired_length_minutes} min
              </div>
              <h1 className="mt-1.5 font-display text-3xl sm:text-4xl font-semibold">{s.title}</h1>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] text-xs font-semibold" data-testid="story-status-chip">{STATUS_LABEL[s.status] || s.status}</div>
            </div>
            <button data-testid="story-begin-interview-button" onClick={() => navigate(`/stories/${storyId}/interview/${nextChapter?.key || "childhood"}`)}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
              <Play size={18} /> {s.coverage?.answered > 0 ? "Continue interview" : "Begin first interview"}
            </button>
          </div>
        </div>

        {/* stat blocks */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatBlock icon={Mic} label="Interview" value={`${s.coverage?.done || 0}/${s.coverage?.total || 9}`} sub="chapters complete" />
          <StatBlock icon={Images} label="Archive vault" value={archiveTotal} sub={`${archive.memories || 0} memories`} />
          <StatBlock icon={FileText} label="Narration" value={s.script_approved_pct != null ? `${s.script_approved_pct}%` : "—"} sub="approved" />
          <StatBlock icon={Film} label="Film" value={STATUS_LABEL[s.status] || "—"} sub="render status" />
        </div>

        {/* chapter film strip */}
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold">Life chapters</h2>
            <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{s.coverage?.answered || 0} answers gathered</span>
          </div>
          <div className="mt-3 flex gap-3 overflow-x-auto pb-2" data-testid="story-chapter-strip">
            {(s.chapters || []).map((ch) => (
              <ChapterChip key={ch.key} ch={ch} onClick={() => navigate(`/stories/${storyId}/interview/${ch.key}`)} />
            ))}
          </div>
        </section>

        {/* action cards */}
        <section className="mt-8 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
            <div className="h-10 w-10 grid place-items-center rounded-xl bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))]"><Images size={18} /></div>
            <h3 className="mt-3 font-display text-lg font-semibold">Gather the archive</h3>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Collect photos, home videos, letters and voice memories from the whole family.</p>
            <button data-testid="story-open-archive-button" onClick={() => navigate(`/stories/${storyId}/archive`)}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--accent))]">Open the vault <ChevronRight size={15} /></button>
          </div>
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
            <div className="h-10 w-10 grid place-items-center rounded-xl bg-[hsl(var(--theme-accent-soft))] text-[hsl(var(--accent))]"><Users size={18} /></div>
            <h3 className="mt-3 font-display text-lg font-semibold">Invite the family</h3>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Send each relative a personal AI-guided interview link — track who opens it and records. No account needed.</p>
            <button data-testid="story-invite-family-button" onClick={() => navigate(`/stories/${storyId}/invites`)}
              className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--accent))]">Manage invitations <ChevronRight size={15} /></button>
          </div>
        </section>

        <StudioPanel story={s} storyId={storyId} onChange={load} />
      </main>
    </div>
  );
}
